import React from "react";
import { NavLink } from "react-router-dom";
import Logo from "../images/logo.png";
import Avatar from "../images/avatar.png";

const SECTIONS = [
    {
        label: "Home",
        items: [
            { key: "scan",    to: "/",        end: true,  icon: "fa-barcode",      label: "Scan" },
            { key: "profile", to: "/profile", end: false, icon: "fa-circle-user",  label: "User Profile", always: true },
        ],
    },
    {
        label: "Insights",
        permGroup: ["analytics"],
        items: [
            { key: "analytics", to: "/analytics", icon: "fa-chart-pie", label: "Analytics & Forecasting" },
        ],
    },
    {
        label: "Inventory",
        permGroup: ["upc_data", "export_excel", "purchase_records", "sale_records"],
        items: [
            { key: "upc_data",     to: "/my-upc-scan-slip",   icon: "fa-list-check",         label: "Scanned UPC Data" },
            { key: "upc_data",     to: "/inventory-lookup",   icon: "fa-chart-line",         label: "Inventory Lookup" },
            { key: "purchase_records", to: "/purchase-records", icon: "fa-cart-shopping",   label: "Purchase Records" },
            { key: "sale_records",     to: "/sale-records",     icon: "fa-cash-register",    label: "Sale Records" },
            { key: "export_excel", to: "/export-excel-file",  icon: "fa-file-excel",         label: "Export Excel" },
        ],
    },
    {
        label: "Products",
        permGroup: ["products"],
        items: [
            { key: "products", to: "/add-products",    icon: "fa-plus-circle",   label: "Add Product" },
            { key: "products", to: "/import-products", icon: "fa-file-import",   label: "Import Products" },
            { key: "products", to: "/product-catalog", icon: "fa-boxes-stacked", label: "Product Catalog" },
        ],
    },
];

const Sidebar = ({ user, headerRef, showHeader, logoutUser, handleClick }) => {
    const perms = user?.permissions || [];
    const isAdmin = user?.role === "Admin";
    const canSee = (key) => isAdmin || perms.includes(key);

    const navLink = ({ to, end, icon, label, id }) => (
        <NavLink
            key={to}
            to={to}
            end={end}
            id={id}
            onClick={handleClick}
            className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-2.5 rounded-xl mx-3 my-0.5 transition-all duration-200 relative overflow-hidden
                ${isActive
                    ? "bg-gradient-to-r from-[#5f2dde] to-[#0085db] text-white shadow-md"
                    : "text-slate-600 hover:bg-[var(--color-2-light)] hover:text-[var(--color-2)]"
                }`
            }
        >
            {({ isActive }) => (
                <>
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0 transition-all
                        ${isActive
                            ? "bg-white/20 text-white"
                            : "bg-slate-100 text-slate-500 group-hover:bg-[var(--color-2-light)] group-hover:text-[var(--color-2)]"
                        }`}
                    >
                        <i className={`fa-solid ${icon}`} />
                    </span>
                    <span className="font-semibold text-sm leading-none">{label}</span>
                    {isActive && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white/70 flex-shrink-0" />
                    )}
                </>
            )}
        </NavLink>
    );

    return (
        <>
            {/* Mobile backdrop */}
            {showHeader && (
                <div
                    className="fixed inset-0 bg-black/40 z-[99999999] lg:hidden backdrop-blur-sm"
                    onClick={handleClick}
                />
            )}

            {/* Sidebar panel */}
            <div
                ref={headerRef}
                className={`w-[78vw] sm:w-[320px] lg:w-[240px] fixed h-[100vh] top-0 left-0 z-[100000000] transition-transform duration-300 ease-in-out
                    ${showHeader ? "translate-x-0" : "-translate-x-full"}`}
            >
                <div className="w-full h-full bg-white flex flex-col shadow-2xl lg:shadow-[4px_0_24px_rgba(0,0,0,0.06)]">

                    {/* Header — Logo + Close */}
                    <div className="flex items-center justify-between px-4 pt-5 pb-3 border-b border-slate-100">
                        <NavLink to="/" onClick={handleClick}>
                            <img src={Logo} alt="logo" className="h-8 object-contain" />
                        </NavLink>
                        <button
                            onClick={handleClick}
                            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors"
                        >
                            <i className="fa-solid fa-xmark text-sm" />
                        </button>
                    </div>

                    {/* Store badge (for store users) */}
                    {user?.role === "Store User" && (
                        <div className="mx-4 mt-3 px-3 py-2 rounded-xl bg-gradient-to-r from-[#5f2dde]/10 to-[#0085db]/10 border border-[#0085db]/20 flex items-center gap-2">
                            <i className="fa-solid fa-store text-[var(--color-2)] text-xs" />
                            <span className="text-xs font-semibold text-[var(--color-2)] truncate">Store Panel</span>
                        </div>
                    )}

                    {/* Navigation */}
                    <nav className="flex-1 overflow-y-auto py-3 my-scroll">
                        {SECTIONS.map(({ label, items }) => {
                            const visibleItems = items.filter(item => item.always || canSee(item.key));
                            if (visibleItems.length === 0) return null;

                            return (
                                <div key={label} className="mb-2">
                                    <div className="flex items-center gap-2 px-4 py-1.5 mb-1">
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
                                        <div className="flex-1 h-px bg-slate-100" />
                                    </div>
                                    {visibleItems.map(item =>
                                        navLink({ ...item, id: item.to })
                                    )}
                                </div>
                            );
                        })}
                    </nav>

                    {/* User card */}
                    <div className="border-t border-slate-100 p-3">
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                            <div className="relative flex-shrink-0">
                                <img
                                    src={user?.avatar_url || Avatar}
                                    alt=""
                                    className="w-9 h-9 rounded-full object-cover ring-2 ring-[var(--color-2-light)]"
                                />
                                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-slate-800 truncate">{user?.name}</p>
                                <p className="text-xs text-slate-400 truncate">{user?.role}</p>
                            </div>
                            <button
                                onClick={logoutUser}
                                title="Logout"
                                className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0 transition-colors"
                            >
                                <i className="fa-solid fa-arrow-right-from-bracket text-xs" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
